/**
 * Reads Rey's rendered answer tables and writes them out as a spreadsheet.
 *
 * Both download buttons read the RENDERED table, not the source markdown, so the file matches what
 * the person is looking at — server-side corrections (FactualityVerifier re-foots total rows), the
 * single-record vertical pivot, and the tables built from chart artifacts' tableData all included.
 *
 * ExcelJS is imported on demand: it is about 1 MB minified, and nobody should pay for it until they
 * actually ask for a workbook. vite.config.mjs keeps it out of the eagerly loaded vendor chunk.
 */

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** Beyond this many significant digits Excel silently rounds, so an ID that long stays text. */
const MAX_NUMERIC_DIGITS = 15;

const MIN_COLUMN_WIDTH = 8;
const MAX_COLUMN_WIDTH = 50;

/**
 * The cleaned text of every row in a rendered table, one array of cell strings per row.
 * Header and body rows alike; a row with no cells is skipped.
 */
export function readTableRows(tableElement) {
  const rows = [];

  tableElement.querySelectorAll('tr').forEach(tr => {
    const cells = Array.from(tr.querySelectorAll('th, td')).map(cell => {
      // A verified value renders as <span class="fact-cited">VALUE<sup class="fact-cite">…source…</sup></span>.
      // Reading textContent off the cell directly would drag the whole citation tooltip into the
      // export, so drop the <sup> from a clone before taking the text.
      const clone = cell.cloneNode(true);
      clone.querySelectorAll('.fact-cite').forEach(sup => sup.remove());
      // A <br> carries no text, so textContent would glue the lines together ("12 Main StDallas TX").
      clone.querySelectorAll('br').forEach(br => br.replaceWith(' '));
      return (clone.textContent || '').replace(/\s+/g, ' ').trim();
    });
    if (cells.length > 0) rows.push(cells);
  });

  return rows;
}

/** A rendered table's rows, plus whether its first row is a header (so it can be bolded and frozen). */
export function readTable(tableElement) {
  const firstRow = tableElement.querySelector('tr');
  const hasHeader = !!firstRow && (!!firstRow.closest('thead') ||
    (firstRow.querySelectorAll('td').length === 0 && firstRow.querySelectorAll('th').length > 0));
  return { rows: readTableRows(tableElement), hasHeader };
}

// Optional "(", optional sign, optional "$", digits (comma-grouped or not), optional decimals,
// optional "%", optional ")". US formatting only — that is what Rey's answers use; anything else
// (a de-DE "1.234,56", a date, a phone number, a VIN) fails the match and stays text.
const NUMBER_PATTERN = /^(\()?([+-])?(\$)?(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d+))?(%)?(\))?$/;

/**
 * Turns a cell's text into a real number when it plainly is one, so the sheet can sum and sort it.
 * Returns { value, numFmt } for a number, or { value: text } when the text should stay as written.
 *
 * Deliberately conservative — a value that is not obviously a quantity stays text:
 *   - a leading zero ("0020601", "00123") is an identifier; as a number it would lose the zeros
 *   - more than 15 significant digits would be rounded by Excel
 *   - an unbalanced parenthesis, or a sign inside parentheses, is not an accounting negative
 */
export function parseCellValue(text) {
  const raw = String(text ?? '').trim();
  const match = NUMBER_PATTERN.exec(raw);
  if (!match) return { value: raw };

  const [, openParen, sign, dollar, intPart, fraction = '', percent, closeParen] = match;
  if (!!openParen !== !!closeParen) return { value: raw };
  if (openParen && sign) return { value: raw };

  const intDigits = intPart.replace(/,/g, '');
  if (intDigits.length > 1 && intDigits[0] === '0') return { value: raw };
  if ((intDigits + fraction).replace(/^0+/, '').length > MAX_NUMERIC_DIGITS) return { value: raw };

  let value = Number(`${intDigits}${fraction ? '.' + fraction : ''}`);
  if (sign === '-' || openParen) value = -value;
  if (percent) value = value / 100;
  // -0 would display as "-0" in some viewers.
  if (value === 0) value = 0;

  const decimals = fraction ? '.' + '0'.repeat(fraction.length) : '';
  let numFmt;
  if (percent) {
    numFmt = `0${decimals}%`;
  } else if (dollar) {
    const positive = `"$"#,##0${decimals}`;
    numFmt = openParen ? `${positive};(${positive})` : `${positive};-${positive}`;
  } else if (intPart.includes(',')) {
    numFmt = openParen ? `#,##0${decimals};(#,##0${decimals})` : `#,##0${decimals}`;
  } else if (fraction || openParen) {
    numFmt = openParen ? `0${decimals};(0${decimals})` : `0${decimals}`;
  }

  return numFmt ? { value, numFmt } : { value };
}

// Stand-ins for "no value". They neither make a column numeric nor stop it from being numeric,
// and are written exactly as shown.
const PLACEHOLDER_PATTERN = /^(|-+|—|–|n\/a)$/i;

export function isPlaceholder(text) {
  return PLACEHOLDER_PATTERN.test(String(text ?? '').trim());
}

/**
 * Which columns of a table's body should be written as numbers: those where every non-placeholder
 * cell parses as one, and at least one does. Decided per COLUMN rather than per cell, because a
 * column of identifiers is only mostly numeric — one "200531A" among RO numbers means the column is
 * text, and typing the other cells as numbers would leave it half numbers, half text, which sorts
 * and filters badly and no longer reads the way it did on screen.
 */
export function numericColumns(bodyRows) {
  const verdicts = new Array(Math.max(0, ...bodyRows.map(cells => cells.length)));
  bodyRows.forEach(cells => {
    cells.forEach((text, colIndex) => {
      if (verdicts[colIndex] === false || isPlaceholder(text)) return;
      verdicts[colIndex] = typeof parseCellValue(text).value === 'number';
    });
  });
  return Array.from(verdicts, verdict => verdict === true);
}

/**
 * Builds one workbook with a worksheet per table — "Table 1", "Table 2", … in the order given.
 * Header cells stay text. Body cells in a column numericColumns accepts go through parseCellValue;
 * every other cell is written as the text shown. Resolves to an .xlsx Blob.
 */
export async function buildTablesWorkbook(tables) {
  const module = await import('exceljs');
  const ExcelJS = module.default || module;
  const workbook = new ExcelJS.Workbook();

  tables.forEach((table, index) => {
    const sheet = workbook.addWorksheet(`Table ${index + 1}`);
    const widths = [];
    const numeric = numericColumns(table.hasHeader ? table.rows.slice(1) : table.rows);

    table.rows.forEach((cells, rowIndex) => {
      const isHeader = table.hasHeader && rowIndex === 0;
      const row = sheet.getRow(rowIndex + 1);
      cells.forEach((text, colIndex) => {
        const cell = row.getCell(colIndex + 1);
        if (isHeader) {
          cell.value = text;
          cell.font = { bold: true };
        } else if (!numeric[colIndex] || isPlaceholder(text)) {
          cell.value = text;
        } else {
          const parsed = parseCellValue(text);
          cell.value = parsed.value;
          if (parsed.numFmt) cell.numFmt = parsed.numFmt;
        }
        widths[colIndex] = Math.max(widths[colIndex] || 0, text.length);
      });
    });

    widths.forEach((width, colIndex) => {
      sheet.getColumn(colIndex + 1).width = Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, width + 2));
    });
    if (table.hasHeader) sheet.views = [{ state: 'frozen', ySplit: 1 }];
  });

  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], { type: XLSX_MIME });
}
